import { useState } from "react";
import { useIntl } from "react-intl";
import { Dialog } from "@/components/common/Dialog";
import { Button } from "@/components/design/Button";
import { SegmentedControl } from "@/components/design/SegmentedControl";

/** The file a notebook is exported as; PDF is the default (spark#1252). */
export type StudioExportFormat = "markdown" | "pdf";

/** The PDF | Markdown switch, the same in the export dialog and on the final version's own export button. */
export function StudioExportFormatToggle({ id, value, onChange }: { id: string; value: StudioExportFormat; onChange: (format: StudioExportFormat) => void }) {
  const intl = useIntl();
  return (
    <SegmentedControl<StudioExportFormat>
      id={id}
      size="sm"
      aria-label={intl.formatMessage({ id: "studio.exportFormatLabel", defaultMessage: "File format" })}
      options={[
        { value: "pdf", label: intl.formatMessage({ id: "studio.exportFormatPdf", defaultMessage: "PDF" }) },
        { value: "markdown", label: intl.formatMessage({ id: "studio.exportFormatMarkdown", defaultMessage: "Markdown" }) },
      ]}
      value={value}
      onChange={onChange}
    />
  );
}

// Exporting asks what goes out: the notebook with the assistant's answers, only the user's notes, or a final
// version the assistant writes first (it appears in the notebook and is exported from there). The format on top
// holds for all three (@adr SPARK-0062-studio-notebooks-export-as-pdf@1); the page mounts the dialog for each
// opening, so it always starts at PDF. While a PDF is being made the two that save a file wait as PDF, like the
// final version's own button; as Markdown they stay open. The final version makes no file here, only a prompt in
// the notebook, so it never waits for one (spark#1252).
export function StudioExportDialog({ open, finalDisabled, pdfBusy, onClose, onWithAnswers, onNotesOnly, onFinal }: {
  open: boolean;
  finalDisabled: boolean;
  pdfBusy: boolean;
  onClose: () => void;
  onWithAnswers: (format: StudioExportFormat) => void;
  onNotesOnly: (format: StudioExportFormat) => void;
  onFinal: (format: StudioExportFormat) => void;
}) {
  const intl = useIntl();
  const [format, setFormat] = useState<StudioExportFormat>("pdf");
  const waiting = pdfBusy && format === "pdf";
  const option = (id: string, label: string, hint: string, onClick: () => void, disabled = false) => (
    <li className="flex flex-col gap-1">
      <Button id={id} variant="secondary" disabled={disabled} onClick={onClick}>{label}</Button>
      <p className="smycles-field-meta text-ink-muted">{hint}</p>
    </li>
  );
  return (
    <Dialog open={open} id="studio-export-dialog" onClose={onClose} dismiss="light" title={intl.formatMessage({ id: "studio.exportTitle", defaultMessage: "Export notebook" })}>
      <div className="mb-4">
        <StudioExportFormatToggle id="studio-export-format" value={format} onChange={setFormat} />
      </div>
      <ul className="flex flex-col gap-4">
        {option(
          "studio-export-with-answers",
          intl.formatMessage({ id: "studio.exportWithAnswers", defaultMessage: "With the assistant's answers" }),
          intl.formatMessage({ id: "studio.exportWithAnswersHint", defaultMessage: "Your notes and the questions with their answers, in the notebook's order." }),
          () => onWithAnswers(format),
          waiting,
        )}
        {option(
          "studio-export-notes-only",
          intl.formatMessage({ id: "studio.exportNotesOnly", defaultMessage: "Only my notes" }),
          intl.formatMessage({ id: "studio.exportNotesOnlyHint", defaultMessage: "Just what you wrote, without the assistant." }),
          () => onNotesOnly(format),
          waiting,
        )}
        {option(
          "studio-export-final",
          intl.formatMessage({ id: "studio.exportFinal", defaultMessage: "Create a final version with the assistant" }),
          intl.formatMessage({ id: "studio.exportFinalHint", defaultMessage: "The assistant sums up your notes. The result appears in the notebook, where you export it." }),
          () => onFinal(format),
          finalDisabled,
        )}
      </ul>
    </Dialog>
  );
}
